'use client';

import {
  ArrowRightIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  DocumentTextIcon,
  PhoneIcon,
  ShieldCheckIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useState } from 'react';
import LandingLayout from '../components/LandingLayout';
import RelatedArticles from '../components/RelatedArticles';
import WebsiteDemo from '../components/WebsiteDemo';
import { useAuth } from '../contexts/AuthContextNext';
import { trackButtonClick, trackPhoneClick } from '../lib/analytics';
import { getPlanDisplayPrice, PRICING_PLANS } from '../types/pricing';

export default function Landing() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { isAuthenticated, isInitialized } = useAuth();
  const pricingContext = { isAuthenticated: !isInitialized || isAuthenticated };
  const starterDisplayPrice = getPlanDisplayPrice(PRICING_PLANS[0], pricingContext);

  const features = [
    {
      icon: PhoneIcon,
      title: "AI Call Handling",
      description: "Natural conversations that understand context and respond intelligently to every customer inquiry."
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: "Smart FAQ Responses",
      description: "Instantly answers questions about your hours, services, pricing, and availability using your custom knowledge base."
    },
    {
      icon: DocumentTextIcon,
      title: "Call Transcripts",
      description: "Get detailed transcripts of every conversation delivered straight to your email inbox."
    },
    {
      icon: ClockIcon,
      title: "24/7 Availability",
      description: "Never miss another call-your AI receptionist is always on duty, even when you're not."
    },
    {
      icon: ShieldCheckIcon,
      title: "Automatic Spam Protection",
      description: "Automatically blocks notifications from 10.5M+ known spam numbers while keeping calls logged for your records."
    },
    {
      icon: CheckCircleIcon,
      title: "Easy Setup",
      description: "Start taking calls in minutes with your dedicated phone number and personalized responses."
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: "Call Recordings & SMS Summaries",
      description: "Listen to call recordings and get text message summaries of every conversation for quick review."
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      business: "Sunshine Salon",
      initials: "SJ",
      rating: 5,
      text: "Greet has been a total game-changer for my salon. It handles all our booking calls perfectly, and I haven't missed a potential client since we started using it."
    },
    {
      name: "Mike Rodriguez",
      business: "Rodriguez Plumbing",
      initials: "MR", 
      rating: 5,
      text: "My customers get instant answers about pricing and availability, even at 2 AM. It's like having a professional receptionist who never sleeps."
    },
    {
      name: "Dr. Emily Chen",
      business: "Downtown Dental",
      initials: "EC",
      rating: 5,
      text: "The call transcripts are incredibly helpful-I can see exactly what patients are asking about and adjust our services accordingly."
    }
  ];

  const faqs = [
    {
      question: "How quickly can I get my AI receptionist up and running?",
      answer: "Setup takes just minutes! After signing up, you'll get a dedicated phone number and can customize your responses right away. Your AI receptionist will be answering calls within 5 minutes."
    },
    {
      question: "What types of businesses work best with Greet?",
      answer: "Greet works great for service professionals (plumbers, electricians, cleaners), retail businesses (salons, coffee shops, boutiques), medical practices, law offices, and any business that gets regular customer calls with common questions."
    },
    {
      question: "How natural does the AI voice sound?",
      answer: "Our AI uses advanced voice technology that sounds remarkably natural and professional. Most customers don't even realize they're speaking with an AI-they just think you have an excellent receptionist!"
    },
    {
      question: "Can I customize what the AI says to my customers?",
      answer: "Absolutely! You can customize responses for your specific business hours, services, pricing, and frequently asked questions. The AI learns your business details and speaks in your brand voice."
    },
    {
      question: "What happens if the AI can't answer a customer's question?",
      answer: "When the AI encounters a question it can't handle, it politely tells the caller that someone will call them back, then sends you a transcript with their contact information so you can follow up personally."
    },
    {
      question: "Can I use my existing business phone number?",
      answer: "Yes! You can either get a new dedicated number through Greet or forward your existing business line to your AI receptionist. Both options work perfectly and setup is seamless."
    },
    {
      question: "How does spam detection work?",
      answer: "Greet automatically protects you from spam calls using an industry-leading database of 10.5M+ known spam numbers. Spam and blocked calls still appear in your call logs for your records, but you won't receive notifications or be billed for them."
    }
  ];

  return (
    <LandingLayout>
      {/* Demo Section */}
      <section className="bg-gradient-to-br from-green-100 via-blue-50 to-purple-100 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-r from-green-400/10 to-blue-400/10"></div>
        <div className="absolute top-10 left-10 w-20 h-20 bg-green-400/20 rounded-full blur-xl"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-blue-400/20 rounded-full blur-xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-12 relative">
          <div className="text-center">
            <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-blue-600">
                Try Greet Live
              </span>
              <br />
              <span className="text-gray-900">Create Your AI Right Now</span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-700 mb-8 max-w-3xl mx-auto font-medium">
              Enter your website URL and we&apos;ll create a custom AI phone agent
              <br />
              that knows your business. <span className="text-green-600 font-semibold">No signup required.</span>
            </p>
            
            {/* Demo card */}
            <div className="bg-white text-left rounded-3xl shadow-2xl p-8 md:p-12 max-w-4xl mx-auto relative">
              <WebsiteDemo
                showBrowserMockup={true}
                showVoiceSelector={true}
                showPromptSection={true}
                showSignupCTA={true}
                analyticsContext="homepage_demo"
                isAuthenticated={isAuthenticated}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                AI Phone Receptionist
              </span>
              <br />
              <span className="text-gray-900">That Never Misses a Call</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Greet is your AI-powered phone receptionist that answers calls, books appointments, handles common questions, and more-24/7. Focus on running your business while we handle your phone.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                href={isAuthenticated ? "/app" : "/register"}
                onClick={() => trackButtonClick(isAuthenticated ? "go_to_dashboard" : "try_greet_free", "homepage_hero")}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              >
                {isAuthenticated ? "Go to Dashboard" : "Try Greet for Free"}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need in One Simple Solution
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Professional phone answering that scales with your business
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center p-6 rounded-xl border border-gray-100 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
                <feature.icon className="h-12 w-12 text-blue-600 mx-auto mb-4 transition-transform duration-300 group-hover:scale-125 group-hover:text-blue-700" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2 transition-colors duration-300 group-hover:text-blue-600">{feature.title}</h3>
                <p className="text-gray-600 transition-colors duration-300 group-hover:text-gray-700">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get your AI receptionist up and running in just three simple steps
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center hover:shadow-lg hover:-translate-y-2 transition-all duration-300 group p-6 rounded-xl bg-gray-50 group-hover:bg-white">
              <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4 transition-transform duration-300 group-hover:scale-125">1</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 transition-colors duration-300 group-hover:text-blue-600">Set Up Your Knowledge Base</h3>
              <p className="text-gray-600 transition-colors duration-300 group-hover:text-gray-700">Add your business hours, services, pricing, and frequently asked questions that customers typically ask.</p>
            </div>
            <div className="text-center hover:shadow-lg hover:-translate-y-2 transition-all duration-300 group p-6 rounded-xl bg-gray-50 group-hover:bg-white">
              <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4 transition-transform duration-300 group-hover:scale-125">2</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 transition-colors duration-300 group-hover:text-blue-600">Get Your Free Phone Number</h3>
              <p className="text-gray-600 transition-colors duration-300 group-hover:text-gray-700">Choose a new dedicated number or forward your existing business line to your AI receptionist.</p>
            </div>
            <div className="text-center hover:shadow-lg hover:-translate-y-2 transition-all duration-300 group p-6 rounded-xl bg-gray-50 group-hover:bg-white">
              <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4 transition-transform duration-300 group-hover:scale-125">3</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 transition-colors duration-300 group-hover:text-blue-600">Start Answering Calls</h3>
              <p className="text-gray-600 transition-colors duration-300 group-hover:text-gray-700">Your AI receptionist handles calls instantly and emails you detailed transcripts of every conversation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Trusted by Small Businesses Everywhere
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real stories from business owners who never miss a call anymore
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-xl hover:shadow-lg hover:-translate-y-2 transition-all duration-300 group hover:bg-white">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current transition-colors duration-300 group-hover:text-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 transition-colors duration-300 group-hover:text-gray-900">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mr-3 transition-colors duration-300 group-hover:bg-blue-200">
                    <span className="text-blue-600 font-semibold text-sm transition-colors duration-300 group-hover:text-blue-700">{testimonial.initials}</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 transition-colors duration-300 group-hover:text-blue-600">{testimonial.name}</div>
                    <div className="text-gray-600 text-sm transition-colors duration-300 group-hover:text-gray-700">{testimonial.business}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Learn How AI Can Transform Your Business
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get practical tips and real examples of how small businesses use AI phone receptionists to grow faster
            </p>
          </div>
          
          <RelatedArticles 
            title="Latest Insights"
            showCount={3}
          />
          
          <div className="text-center mt-12">
            <Link 
              href="/blog"
              onClick={() => trackButtonClick("view_all_articles", "homepage_blog")}
              className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              View All Articles
              <ArrowRightIcon className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing Preview Section */}
      <section id="pricing" className="bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Plans start at just{' '}
              {starterDisplayPrice.showSale ? (
                <>
                  <span className="line-through text-gray-400">{starterDisplayPrice.basePrice}</span>{' '}
                  <span className="font-semibold text-green-700">{starterDisplayPrice.price}</span>
                </>
              ) : (
                starterDisplayPrice.price
              )}
              {' '} - much cheaper than hiring a receptionist
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-12 text-center border-2 border-blue-200">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              {PRICING_PLANS.map((plan) => {
                const displayPrice = getPlanDisplayPrice(plan, pricingContext);

                return (
                  <div key={plan.id} className="text-center">
                    {displayPrice.showSale && (
                      <div className="text-lg font-semibold text-gray-400 line-through mb-1">
                        {displayPrice.basePrice.replace('/month', '')}
                      </div>
                    )}
                    <div className="text-4xl font-bold text-blue-600 mb-2">
                      {displayPrice.price.replace('/month', '')}
                    </div>
                    <div className="text-gray-700 font-semibold">{plan.name} Plan</div>
                    <div className="text-sm text-gray-600">
                      {plan.callsPerMonth} calls/month{plan.id === 'growth' ? ' + scheduling' : plan.id === 'professional' ? ' + CRM' : ''}
                    </div>
                    {displayPrice.showSale && (
                      <div className="mt-2 inline-flex rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800">
                        {displayPrice.label}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-center justify-center gap-8 text-sm text-gray-700">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                  7-day free trial
                </div>
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                  Cancel anytime
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                href="/pricing"
                onClick={() => trackButtonClick("view_pricing", "homepage_pricing_preview")}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
              >
                View All Plans & Features
                <ArrowRightIcon className="h-5 w-5 ml-2" />
              </Link>
              <Link 
                href={isAuthenticated ? "/app" : "/register"}
                onClick={() => trackButtonClick(isAuthenticated ? "go_to_dashboard" : "start_free_trial", "homepage_pricing_preview")}
                className="bg-white border border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              >
                {isAuthenticated ? "Go to Dashboard" : "Start Free Trial"}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="bg-white">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Get answers to the most common questions about Greet
            </p>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <ChevronDownIcon 
                    className={`h-5 w-5 text-gray-500 transform transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                <div className={`px-6 text-gray-600 transition-all duration-300 ${
                  openFaq === index ? 'pb-4 max-h-96' : 'pb-0 max-h-0'
                } overflow-hidden`}>
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 lg:px-8 py-16 lg:py-20 relative z-10">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-block mb-6 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
              <span className="text-white text-sm font-semibold">✨ Join Now</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Stop Losing Calls.
              <br />
              Start Growing Today.
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto leading-relaxed">
              Get your AI receptionist set up in 5 minutes. Answer calls 24/7. Book appointments automatically. Watch your business grow.
            </p>
            
            {/* Trust indicators */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-10 text-sm text-blue-100">
              <div className="flex items-center justify-center gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-300" />
                <span>7-day free trial</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-300" />
                <span>Easy setup</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-300" />
                <span>Cancel anytime</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                href={isAuthenticated ? "/app" : "/register"}
                onClick={() => trackButtonClick(isAuthenticated ? "go_to_dashboard" : "start_free_trial", "homepage_cta_bottom")}
                className="group bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-2xl inline-flex items-center justify-center gap-2"
              >
                {isAuthenticated ? "Go to Dashboard" : "Start Your Free Trial"}
                <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link 
                href="tel:+14158773166"
                onClick={() => trackPhoneClick('+14158773166', 'homepage_cta_phone')}
                className="group border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 backdrop-blur-sm inline-flex items-center justify-center gap-2"
              >
                <PhoneIcon className="h-5 w-5" />
                Call Demo
              </Link>
            </div>

            {/* Bottom text */}
            <p className="text-blue-100 text-sm mt-8">
              Most businesses see results within their first week.
            </p>
          </div>
        </div>
      </section>

    </LandingLayout>
  );
} 
